import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { customerServices } from '../../lib/supabase';
import type { Customer } from '../../lib/supabase';

const Overview = () => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!id) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await customerServices.getById(id);
        if (error) throw error;
        setCustomer(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id]);

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!customer) return <div className="text-center p-4">No customer found</div>;

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="space-y-6">
        {/* Basic Info Section */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Name</p>
              <p className="mt-1">{customer.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="mt-1">{customer.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Current Plan</p>
              <p className="mt-1">{customer.plan}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${customer.status === 'Active' ? 'bg-green-100 text-green-800' : 
                  customer.status === 'Trial' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-red-100 text-red-800'}`}>
                  {customer.status}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Metrics Section */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Monthly Recurring Revenue</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">${customer.mrr}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Health Score</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{customer.health_score}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Join Date</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {customer.join_date ? new Date(customer.join_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
